import { SSE } from 'sse.js';

import { env } from '@/config/env';

type Listener = (ev: MessageEvent) => void;

type EventName = string;

const listenersByEvent: Map<EventName, Set<Listener>> = new Map();

let eventSource: SSE | null = null;
const connectionUrl = `${env.VITE_HITPHONE_DESKTOP_URL.replace(/\/$/, '')}/events`;

const ensureInternalListener = (eventName: EventName) => {
  if (!eventSource) return;

  const marker = `__internal_${eventName}` as const;
  // @ts-expect-error use symbol on eventSource to mark internal handlers
  if (eventSource[marker]) return;

  const internalHandler: EventListener = (ev) => {
    const set = listenersByEvent.get(eventName);
    if (!set || set.size === 0) return;
    set.forEach((fn) => {
      try {
        fn(ev as MessageEvent);
      } catch (err) {
        console.error('[DesktopSSE] Listener error for', eventName, err);
      }
    });
  };

  eventSource.addEventListener(eventName, internalHandler);
  // @ts-expect-error store marker so we don't re-add
  eventSource[marker] = true;
};

const connect = () => {
  if (eventSource) return;

  try {
    eventSource = new SSE(connectionUrl, {
      autoReconnect: true,
      reconnectDelay: 5000,
      maxRetries: null,
      useLastEventId: false,
      start: true,
    });

    eventSource.addEventListener('open', () => {
      console.debug('[DesktopSSE] Connected');
    });

    eventSource.addEventListener('error', (err: Event) => {
      console.error('[DesktopSSE] Error', err);
    });
  } catch (err) {
    console.error('[DesktopSSE] Failed to open SSE', err);
    eventSource = null;
  }
};

export const subscribe = (eventName: EventName, listener: Listener) => {
  if (!eventSource) {
    connect();
  }

  if (!listenersByEvent.has(eventName)) {
    listenersByEvent.set(eventName, new Set());
  }
  listenersByEvent.get(eventName)!.add(listener);

  ensureInternalListener(eventName);

  return () => {
    const set = listenersByEvent.get(eventName);
    if (!set) return;
    set.delete(listener);
  };
};

export const onConnectionDrop = (handler: () => void) => {
  const offError = subscribe('error', () => {
    handler();
  });

  const offAbort = subscribe('abort', () => {
    handler();
  });

  const offReady = subscribe('readystatechange', ((
    ev: Event & { readyState?: number }
  ) => {
    if ((ev as { readyState?: number }).readyState === 2) {
      handler();
    }
  }) as unknown as (ev: MessageEvent) => void);

  return () => {
    offError();
    offAbort();
    offReady();
  };
};
